import React, { useCallback, useMemo, useState, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import * as gradeBenchPanelActions from '@codametrix/ui-common/actions/grade-bench-panel';
import {
  enrichingAnnotationHooks,
  extractCodesFromInvoices,
  useCodeSearch,
  useCreateTransactionAndAddCode,
  useCodeAssessmentUpdate,
  mapExtractedCodetoSuggestedCode
} from '@codametrix/ui-common/amplify-core/annotation';
import { codePredicates } from '@codametrix/ui-common/amplify-core/transaction-updates';
import { 
  AnnotatableDocument, 
  AnnotationRender,
} from '@codametrix/ui-components';
import { HighlightDrawer } from '../highlight-drawer/highlight-drawer';
import { findSpanScopedWorkflowMatchesForSpan } from '../highlight-drawer/rubric-utils';
import { isCodeUniqueInCQATransaction } from '../code-bench-panel/grade-bench-panel-generalized';
import './styles/index.scss';
const CodeTypeMap = {
  procedure: 'PROCEDURE',
  modifier: 'MODIFIER',
  diagnosis: 'DIAGNOSIS'
};

// Maps code type to attribute entity type for API calls
const codeTypeToEntityType = (codeType: string): CQA.AttributeEntityType => {
  switch (codeType) {
    case 'procedure': return 'code_procedure';
    case 'diagnosis': return 'code_diagnosis';
    case 'modifier': return 'code_modifier';
    default: return 'code_procedure'; // fallback
  }
};

export interface AnnotatableDocumentWithDrawerWrapperProps {
  text: string;
  noteId: number;
  spans: Accelerate.SpanAnnotation[];
}

export const AnnotatableDocumentWithDrawerWrapper: React.FC<AnnotatableDocumentWithDrawerWrapperProps> = ({
  text,
  noteId,
  spans
}) => {
  const dispatch = useDispatch();
  const gradeBenchPanelState = useSelector(
    (state: CMx.ApplicationState) => state.gradeBenchPanel
  );
  const { invoices, codeDescriptions, qaRule, fieldDefinitions, cqaCodeAssessmentRiskDictionaries, rubricVersion, caseAttributes } = gradeBenchPanelState;

  // Get project configuration
  const projectConfiguration = qaRule?.data?.project_configuration;
  const caseEvaluationLevel = projectConfiguration?.caseEvaluationLevel ?? false;
  const rubricVersionId = projectConfiguration?.rubricVersionId;

  // Fetch rubric version when rubricVersionId is available, clear it when it is not available
  useEffect(() => {
    if (rubricVersionId) {
      dispatch(gradeBenchPanelActions.fetchRubricVersion({ versionId: rubricVersionId }) as any);
    } else {
      dispatch(gradeBenchPanelActions.clearRubricVersion());
    }
  }, [rubricVersionId, dispatch]);

  const caseId = invoices[0]?.sdCaseId || '';
  const workItemId = invoices[0]?.workitemId;

  // Get codeBench state for code search hook
  const { activeItem, userContext, processInstance, searchResultsMap, editable } = useSelector(
    (state: CMx.ApplicationState) => {
      const codeBench = state.codeBench;
      return {
        activeItem: codeBench?.activeItem || codeBench?.capabilities?.activeItem || ({} as CMxAPI.CaseRun),
        userContext: state.ui.context.activeContext || codeBench?.capabilities?.userContext || codeBench?.userContext || ({} as CMxAPI.OrganizationContext),
        processInstance: codeBench?.processInstance,
        searchResultsMap: {
          v2: codeBench?.codeSearchResultsV2 || {},
          v1: codeBench?.searchResults || {}
        },
        editable: codeBench?.benchEditable?.editable
      };
    }
  );

  // State for drawer and hover
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [selectedSpanId, setSelectedSpanId] = useState<number | null>(null);
  const [hoveredSpan, setHoveredSpan] = useState<Accelerate.SpanAnnotation | null>(null);

  // Track successful and in-flight attribute fetches to prevent duplicate requests
  // without permanently suppressing retries after a failed request.
  const fetchedAttributesRef = useRef<Set<string>>(new Set());
  const pendingAttributesRef = useRef<Set<string>>(new Set());

  const fetchAttributesOnce = useCallback((
    key: string,
    params: CQA.FetchAttributesParams,
  ) => {
    if (fetchedAttributesRef.current.has(key) || pendingAttributesRef.current.has(key)) {
      return;
    }

    pendingAttributesRef.current.add(key);
    const action = gradeBenchPanelActions.fetchAttributes(params) as any;
    const result = dispatch(action) as any;

    void Promise.resolve(result)
      .then((resolvedAction: any) => {
        const finalAction = resolvedAction ?? action;
        if (!finalAction?.error && finalAction?.meta?.requestStatus !== 'rejected') {
          fetchedAttributesRef.current.add(key);
        }
      })
      .catch(() => undefined)
      .finally(() => {
        pendingAttributesRef.current.delete(key);
      });
  }, [dispatch]);

  // Derive selectedSpan from Redux state so rubricAttributes stay in sync
  const selectedSpan = useMemo(() => {
    if (!selectedSpanId) return null;
    // Check loose spans first
    const looseSpan = gradeBenchPanelState.looseSpans?.find(s => s.id === selectedSpanId);
    if (looseSpan) return looseSpan;
    // Check spans attached to codes in invoices
    for (const invoice of invoices || []) {
      for (const transaction of invoice.transactions || []) {
        for (const codes of [transaction.procedures, transaction.diagnoses, transaction.modifiers]) {
          for (const code of codes || []) {
            const found = code.spans?.find(s => s.id === selectedSpanId);
            if (found) return found;
          }
        }
      }
    }
    return null;
  }, [selectedSpanId, gradeBenchPanelState.looseSpans, invoices]);

  // Wrapper to match existing setSelectedSpan call sites
  const setSelectedSpan = useCallback((spanOrNull: Accelerate.SpanAnnotation | null) => {
    setSelectedSpanId(spanOrNull?.id ?? null);
  }, []);

  // Extract codes and calculate available
  const allInvoiceCodes = useMemo(() => 
    extractCodesFromInvoices(invoices || [], codeDescriptions || {}), 
    [invoices, codeDescriptions]
  );

  // Filter available codes: exclude codes attached to any span
  const availableCodes = useMemo(() => {
    return allInvoiceCodes.filter(code => code.spans?.length === 0);
  }, [allInvoiceCodes, selectedSpan]);

  const suggestedCodes: CMxCommonApp.SuggestedCodes = useMemo(() => {
    // Get codes that ARE attached to spans
    const codesWithSpans = allInvoiceCodes.filter(code => code.spans?.length > 0);
    const procedures = codesWithSpans.filter(code => code.codeType === 'procedure').map(mapExtractedCodetoSuggestedCode);
    const diagnoses = codesWithSpans.filter(code => code.codeType === 'diagnosis').map(mapExtractedCodetoSuggestedCode);
    const modifiers = codesWithSpans.filter(code => code.codeType === 'modifier').map(mapExtractedCodetoSuggestedCode);

    return {
      cpt: {
        encounterList: [],
        caseList: procedures,
        codeSourceList: []
      },
      icd: {
        caseList: diagnoses,
        encounterList: [],
        codeSourceList: []
      },
      modifier: {
        caseList: modifiers,
        encounterList: [],
        codeSourceList: []
      }
    };
  }, [allInvoiceCodes]);

  // Use code search hook - pass Redux state values as parameters
  const codeSearch = useCodeSearch({
    dispatch,
    caseId,
    lookupKey: selectedSpan?.id,
    activeItem,
    userContext,
    processInstance,
    searchResultsMap
  });

  // Use transaction creation and code addition hook
  const { createTransactionAndAddCode } = useCreateTransactionAndAddCode(dispatch);

  // Use code assessment update hook
  const { updateCodeAssessment } = useCodeAssessmentUpdate(dispatch);

  // Save rubric answers - Redux reducer handles updating span/code/case attributes in state
  const saveAttribute = useCallback(async (params: CQA.UpdateAttributeParams) => {
    await dispatch(gradeBenchPanelActions.updateAttribute(params) as any);
  }, [dispatch]);

  // Delete rubric answers - Redux reducer handles removing span/code/case attributes in state
  const deleteAttribute = useCallback(async (params: CQA.DeleteAttributeParams) => {
    await dispatch(gradeBenchPanelActions.deleteAttribute(params) as any);
  }, [dispatch]);

  const clearDownstream = useCallback(
    (params: CQA.ClearDownstreamAttributesParams) => {
      dispatch(gradeBenchPanelActions.clearDownstreamAttributes(params));
    },
    [dispatch]
  );

  const revalidate = useCallback(() => {
    dispatch(gradeBenchPanelActions.revalidateWorkItem() as any);
  }, [dispatch]);

  // Convert existing codes for selected span to AttachedCodes format
  const attachedCodes = useMemo((): CQA.AttachedCodes => {
    if (!selectedSpan) {
      return { procedures: [], modifiers: [], diagnoses: [] };
    }

    // Filter codes that have the selected span in their spans array
    const spanCodes = allInvoiceCodes.filter(code =>
      code.spans?.some(span => span.id === selectedSpan.id)
    );

    return {
      procedures: spanCodes.filter(code => code.codeType === 'procedure'),
      modifiers: spanCodes.filter(code => code.codeType === 'modifier'),
      diagnoses: spanCodes.filter(code => code.codeType === 'diagnosis'),
    };
  }, [selectedSpan, allInvoiceCodes]);

  const spanScopeSourceCodes = useMemo((): CQA.AttachedCodes => ({
    procedures: allInvoiceCodes.filter(code => code.codeType === 'procedure'),
    modifiers: allInvoiceCodes.filter(code => code.codeType === 'modifier'),
    diagnoses: allInvoiceCodes.filter(code => code.codeType === 'diagnosis'),
  }), [allInvoiceCodes]);

  // Fetch code answers when drawer opens so source-scoped SPAN questions can inspect
  // SPANS answers even when the owning code is not directly attached to the span.
  useEffect(() => {
    if (drawerOpen && selectedSpan) {
      const codeSources = [
        ...spanScopeSourceCodes.procedures,
        ...spanScopeSourceCodes.modifiers,
        ...spanScopeSourceCodes.diagnoses,
      ];

      // Fetch answers for each code that we haven't already fetched
      codeSources.forEach(code => {
        const codeKey = `${code.codeType}-${code.codeId}`;
        fetchAttributesOnce(codeKey, {
          entityId: code.codeId.toString(),
          entityType: codeTypeToEntityType(code.codeType)
        });
      });
    }
  }, [drawerOpen, selectedSpan?.id, spanScopeSourceCodes, fetchAttributesOnce]);

  // Fetch span attributes when drawer opens - Redux reducer stores them on the span
  useEffect(() => {
    if (!drawerOpen || !selectedSpan) return;

    const spanKey = `span-${selectedSpan.id}`;
    fetchAttributesOnce(spanKey, {
      entityId: String(selectedSpan.id),
      entityType: 'span'
    });
  }, [drawerOpen, selectedSpan?.id, fetchAttributesOnce]);

  useEffect(() => {
    if (!drawerOpen || !selectedSpan) return;

    const matches = findSpanScopedWorkflowMatchesForSpan(
      rubricVersion,
      selectedSpan.id,
      spanScopeSourceCodes,
      caseAttributes,
    );

    matches.forEach(match => {
      const spanScopeAttributeId = match.sourceAttribute.id;
      if (spanScopeAttributeId == null) return;

      const spanScopedKey = `span-${selectedSpan.id}-scope-${spanScopeAttributeId}`;
      fetchAttributesOnce(spanScopedKey, {
        entityId: String(selectedSpan.id),
        entityType: 'span',
        spanScopeAttributeId,
      });
    });
  }, [drawerOpen, selectedSpan?.id, rubricVersion, spanScopeSourceCodes, caseAttributes, fetchAttributesOnce]);

  // Handler for adding a code to an annotation (atomic save)
  const handleAttachCode = useCallback(async (
    code: CQA.ExtractedCode,
    annotation: Partial<Accelerate.SpanAnnotation>
  ) => {
    if (!noteId || !annotation.id) return;

    try {
      const originalCode = allInvoiceCodes.find(c => c.codeId === code.codeId && c.codeType === code.codeType);
      if (!originalCode) {
        throw new Error('Code not found in available codes');
      }

      await enrichingAnnotationHooks.attachCode(dispatch, {
        spanId: annotation.id,
        codeId: originalCode.codeId,
        codeType: CodeTypeMap[code.codeType],
        caseId: invoices[0]?.sdCaseId || '',
        documentId: noteId
      });

      // Fetch attributes for this code if not already fetched
      // This handles the case where a code with existing attributes is attached while drawer is open
      const codeKey = `${code.codeType}-${code.codeId}`;
      fetchAttributesOnce(codeKey, {
        entityId: code.codeId.toString(),
        entityType: codeTypeToEntityType(code.codeType)
      });

      revalidate();
    } catch (error) {
      console.error('Failed to add code:', error);
      throw error;
    }
  }, [noteId, dispatch, invoices, allInvoiceCodes, revalidate, fetchAttributesOnce]);

  // Handler for removing a code from an annotation (atomic save)
  const handleRemoveCode = useCallback(async (
    code: CQA.ExtractedCode,
    annotation: Partial<Accelerate.SpanAnnotation>
  ) => {
    if (!noteId || !annotation.id) throw new Error('No note ID or annotation ID');
    try {
      const originalCode = allInvoiceCodes.find(c => c.codeId === code.codeId && c.codeType === code.codeType);
      if (!originalCode) {
        throw new Error('Code not found in available codes');
      }

      await enrichingAnnotationHooks.detachCode(dispatch, {
        spanId: annotation.id,
        codeId: originalCode.codeId,
        codeType: CodeTypeMap[code.codeType]
      });

      // Clear the cache entry so attributes are re-fetched if code is re-attached
      const codeKey = `${code.codeType}-${code.codeId}`;
      fetchedAttributesRef.current.delete(codeKey);

      revalidate();
    } catch (error) {
      console.error('Failed to remove code:', error);
      throw error;
    }
  }, [noteId, dispatch, allInvoiceCodes, revalidate]);

  // Handler for deleting entire annotation
  const handleDeleteAnnotation = useCallback(async () => {
    if (!selectedSpan) return;

    try {
      await enrichingAnnotationHooks.deleteSpan(dispatch, {
        spanId: selectedSpan.id
      });
      setDrawerOpen(false);
      setSelectedSpan(null);
      revalidate();
    } catch (error) {
      console.error('Failed to delete annotation:', error);
    }
  }, [selectedSpan, dispatch, revalidate]);

  // Handler for text selection to create new annotation
  const handleTextSelection = useCallback(async (
    selectedText: string, 
    startIndex: number, 
    endIndex: number
  ) => {
    try {
      const tempSpan: Accelerate.SpanAnnotation = {
        id: Date.now(),
        beginOffset: startIndex,
        endOffset: endIndex,
        coveredText: selectedText,
        attributes: [],
        documentId: noteId
      };
      
      const spanData: CQA.SpanAnnotationDocument = {
        documentId: noteId.toString(),
        caseId: invoices[0]?.sdCaseId || '',
        spans: [tempSpan]
      };
      
      const createdSpan = await enrichingAnnotationHooks.addEmptySpan(dispatch, spanData);
      
      if(createdSpan){
        setSelectedSpan(createdSpan);
        setDrawerOpen(true);
        revalidate();
      }
    } catch (error) {
      console.error('Failed to create empty span:', error);
    }
  }, [noteId, dispatch, invoices, revalidate]);

  // Handler for span click - opens drawer. Span rubricAttributes are stored in Redux.
  const handleSpanClick = useCallback((
    span: Accelerate.SpanAnnotation,
    index: number,
    event: React.MouseEvent
  ) => {
    setSelectedSpan(span);
    setDrawerOpen(true);
    setHoveredSpan(null);
  }, []);

  // Handler for span hover - shows preview
  const handleSpanHover = useCallback((
    span: Accelerate.SpanAnnotation,
    index: number,
    event: React.MouseEvent
  ) => {
    setHoveredSpan(span);
  }, []);

  // Handler for closing drawer
  const handleCloseDrawer = useCallback(() => {
    setDrawerOpen(false);
    setSelectedSpan(null);
  }, []);

  // Handler for closing annotation preview
  const handleCloseAnnotation = useCallback(() => {
    setHoveredSpan(null);
  }, []);

  // Build code search props for HighlightDrawer
  const codeSearchProps = useMemo((): CQA.CodeSearchProps => {
    if (!selectedSpan) {
      return {
        searchResults: {},
        onCodeSearch: () => {},
        onCodeSearchSelection: () => {}
      };
    }

    const searchResultsKey = selectedSpan.id.toString();
    const searchResults = codeSearch.getSearchResults(selectedSpan.id);

    return {
      searchResults: {
        [searchResultsKey]: searchResults
      },
      onCodeSearch: (codeQuery: CMxCommonApp.CodeQuery) => {
        codeSearch.onCodeSearch(codeQuery, {
          selectedSpan,
          documentId: noteId,
          caseId,
          spans: [selectedSpan]
        }, searchResultsKey);
      },
      onCodeSearchSelection: (codeAction: Accelerate.CodeActionPayload) => {
        const lastInvoice = invoices?.at(-1);
        
        if (!lastInvoice) {
          console.warn('No invoice found for code search selection');
          return;
        }

        // Check if caseEvaluationLevel is false and code is CPT
        const isCPTCode = codePredicates.isSelectionCPT(codeAction.codeInfo);
        const shouldCreateTransaction = !caseEvaluationLevel && isCPTCode;

        if (shouldCreateTransaction) {
          // Create transaction and add code in one operation
          const payload: Accelerate.CodeAddActionPayload = {
            codeInfo: codeAction.codeInfo,
            context: {
              ...codeAction.context,
              invoice: lastInvoice,
              processInstance: processInstance || codeAction.context?.processInstance,
              activeItem: activeItem || codeAction.context?.activeItem
            },
            documentId: noteId,
            caseId,
            spans: [selectedSpan]
          };

          createTransactionAndAddCode(payload);
        } else {
          // Use last invoice's last transaction
          const transactionToUse = lastInvoice.transactions?.at(-1);
          
          if (!transactionToUse) {
            return;
          }

          // Check for duplicate codes in the transaction
          const isUnique = isCodeUniqueInCQATransaction(
            codeAction.codeInfo,
            transactionToUse
          );

          if (!isUnique) {
            // Duplicate code detected, prevent adding
            return;
          }

          codeSearch.onCodeSearchSelection(codeAction, {
            transaction: transactionToUse,
            invoice: lastInvoice,
            documentId: noteId,
            caseId,
            spans: [selectedSpan]
          });
        }
      }
    };
  }, [selectedSpan, codeSearch, noteId, caseId, invoices, caseEvaluationLevel, createTransactionAndAddCode, activeItem, processInstance]);

  // Handler for code assessment/role updates
  const handleCodeAssessmentUpdate = useCallback(async (
    code: CQA.ExtractedCode,
    field: CMxCommonApp.SelectListField,
    fields: CMxCommonApp.CodeLayerField[],
    value: string
  ) => {
    // Find the invoice and transaction using the code's IDs
    const invoiceToUse = invoices?.find(inv => inv.id === code.invoiceId);
    const transactionToUse = invoiceToUse?.transactions?.find(tx => tx.id === code.transactionId);

    if (!invoiceToUse || !transactionToUse) {
      console.warn(`Invoice or transaction not found for code ${code.compositeId}`);
      return;
    }

    // Convert ExtractedCode to the format expected by updateOrAddAssessment
    // The action expects value.id and value.codeType
    let codeTypeValue: 'procedure' | 'diagnosis' | 'modifier';
    if (code.codeType === 'procedure') {
      codeTypeValue = 'procedure';
    } else if (code.codeType === 'diagnosis') {
      codeTypeValue = 'diagnosis';
    } else {
      codeTypeValue = 'modifier';
    }

    const codeValue = {
      id: code.codeId,
      codeType: codeTypeValue,
      code: code.value,
      codeAssessment: code.codeAssessment || null
    };

    // Create the GradeBenchSelectEvent
    const changeEvent: CMxCommonApp.GradeBenchSelectEvent = {
      selection: value,
      context: {
        value: codeValue,
        field: field,
        fields: fields,
        transaction: transactionToUse,
        invoice: invoiceToUse
      }
    };

    // Dispatch the action using the hook
    await updateCodeAssessment(changeEvent);
  }, [updateCodeAssessment, invoices]);

  return (
    <>
      <AnnotatableDocument
        text={text}
        spans={spans}
        isAnnotationRenderPinnable={false}
        renderAnnotation={(span, onCloseCallback) => {
          // Show AnnotationRender preview on hover (single-span path)
          if (hoveredSpan?.id === span.id && !drawerOpen) {
            return (
              <AnnotationRender
                key={span.id}
                mode="read"
                span={span}
                availableCodes={availableCodes}
                existingCodes={allInvoiceCodes?.filter(code =>
                  code.spans?.some(s => s.id === span.id)
                )}
                onClose={handleCloseAnnotation}
                onAttachCode={handleAttachCode}
                onDetachCode={handleRemoveCode}
                onDelete={handleDeleteAnnotation}
              />
            );
          }
          return null;
        }}
        renderPreviewContent={(span) => (
          <AnnotationRender
            key={span.id}
            mode="read"
            span={span}
            availableCodes={availableCodes}
            existingCodes={allInvoiceCodes?.filter(code =>
              code.spans?.some(s => s.id === span.id)
            )}
            onClose={handleCloseAnnotation}
            onAttachCode={handleAttachCode}
            onDetachCode={handleRemoveCode}
            onDelete={handleDeleteAnnotation}
          />
        )}
        enableHover={true}
        enableClick={true}
        closeOnBlur={false}
        onSpanClick={handleSpanClick}
        onSpanHover={handleSpanHover}
        enableTextSelection={true}
        onTextSelection={handleTextSelection}
        onAnnotationClose={handleCloseAnnotation}
      />

      {/* HighlightDrawer - renders on right side via portals */}
      <HighlightDrawer
        isOpen={drawerOpen}
        onClose={handleCloseDrawer}
        size="lg"
        selectedSpan={selectedSpan || undefined}
        attachedCodes={attachedCodes}
        spanScopeSourceCodes={spanScopeSourceCodes}
        availableCodes={availableCodes}
        codeSearch={codeSearchProps}
        caseId={caseId}
        onAttachCode={handleAttachCode}
        onDetachCode={handleRemoveCode}
        onDelete={handleDeleteAnnotation}
        codeAssessmentProps={{
          cptFields: fieldDefinitions?.cptFields,
          icdFields: fieldDefinitions?.icdFields,
          modifierFields: fieldDefinitions?.modifierFields,
          cqaCodeAssessmentRiskDictionaries,
          projectConfiguration,
          onCodeAssessmentUpdate: handleCodeAssessmentUpdate
        }}
        suggestedCodes={suggestedCodes}
        rubricVersion={rubricVersion}
        onAttributeChange={saveAttribute}
        onDeleteAttribute={deleteAttribute}
        editable={editable}
        onClearDownstream={clearDownstream}
        evaluateFn={gradeBenchPanelActions.evaluateWorkflow}
        workItemId={workItemId}
      />
    </>
  );
};
